import { ReactComponent as FavouriteOutlinedIcon } from "images/bookmark.svg";
import {
  addFavouriteCourse,
  removeFavouriteCourse,
} from "../../../../../services/favorites";
import { ReactComponent as FavouriteIcon } from "images/bookmark-filled.svg";
import { ReactComponent as LessonIcon } from "../../../../../images/lesson.svg";
import { ReactComponent as CheckIcon } from "../../../../../images/check_circle_grey.svg";
import {
  isGroupUser,
  isOneOnOneUser,
  isSelfStudyUser,
  isUserStudent,
} from "../../../../../services/authUtil";
import { ReactComponent as TimeIcon } from "../../../../../images/time.svg";
import exerciseIcon from "../../../../../images/exercise_icon_2.svg";
import TalaeraRoutes from "./../../../../routes/TalaeraRoutes";
import LibraryObjectViewType from "../../LibraryObjectViewType";
import { ReactNode, useEffect, useMemo, useState } from "react";
import bookImg from "./../../../../../images/book.svg";
import notchImg from "./../../images/notch.svg";
import { Link, useLocation } from "react-router-dom";
import { getTimeStringFromMinutes } from "services/util";
import { ReactComponent as LevelIcon } from "./../../../../../images/level.svg";
import classnames from "classnames";
import { HStack } from "../../../../Stack";
import { ReactComponent as LockIcon } from "images/lock.svg";
import { LibraryObjectType } from "../../../../../model/library";
import { useTranslation } from "react-i18next";

export type ILibraryObject = {
  id: string;
  name?: string;
  type: string;
  assignment: any;
  description?: string;
  preview_url?: string;
  preview_horizontal_url?: string;
  preview_vertical_url?: string;
  author?: string;
  is_favorite?: boolean;
  progress?: {
    progress: number;
    remaining: { id: string; length: number }[];
    completed: { id: string; length: number };
  };
  children: {
    lessons: number;
  };
  length: number;
  tags: { category: string; name: string }[];
  level: { id: string; name: string };
};

type ObjectCardInfoProps = {
  data: ILibraryObject;
  type: "library-object";
  isProgress: boolean;
};

const ObjectCardInfo = (props: ObjectCardInfoProps) => {
  const isUserNotStudent = !isUserStudent();
  if (!props.data) {
    return null;
  }

  const levelsTag =
    props.type === "library-object"
      ? props.data?.tags?.filter(tag => tag.category === "level").map(tag => tag.name)
      : [props.data?.level?.name];

  let lessonsInfo = "";
  if (props.type === "library-object") {
    if (isUserNotStudent) {
      lessonsInfo = `${props.data.children?.lessons || 0} Lesson${props.data.children?.lessons > 1 ? "s" : ""} total`;
    } else {
      lessonsInfo = `${props.data.progress?.remaining?.length || 0} ${
        (props.data.progress?.remaining?.length || 0) > 1
          ? `Lessons ${Number(props.data.progress?.progress) > 0 ? " left" : "total"}`
          : `Lesson${Number(props.data.progress?.progress) > 0 ? " left" : "total"}`
      }`;
    }
  }

  return (
    <div className="object-card-info">
      {props.type === "library-object" &&
        !!Math.floor(Number(props.data.progress?.progress)) && (
          <div className="object-card-progress">
            <div className="progress-bar">
              <div
                className="progress-inner"
                style={{ width: `${props.data.progress?.progress || 0}%` }}
              />
            </div>
          </div>
        )}

      <div className="info-line">
        {props.type === "library-object" &&
        Number(props.data.progress?.progress) === 100 ? (
          <div className="info-item">
            <CheckIcon />
            100% Completed
          </div>
        ) : (
          <>
            <div className="info-item">
              <div className="info-line-icon">
                <TimeIcon />
              </div>
              <div>
                {props.type === "library-object" &&
                !isUserNotStudent &&
                props.data.type === LibraryObjectType.COURSE
                  ? props.data.progress
                    ? getTimeStringFromMinutes(
                        props.data.progress?.remaining.reduce(
                          (sum, lesson) => sum + lesson.length,
                          0
                        )
                      )
                    : ""
                  : getTimeStringFromMinutes(props.data.length)}
              </div>
            </div>
            {props.type === "library-object" &&
              props.data.type === LibraryObjectType.COURSE && (
                <div className="info-item">
                  <div className="info-line-icon">
                    <LessonIcon />
                  </div>
                  <div>{lessonsInfo}</div>
                </div>
              )}
            {props.type === "library-object" &&
              Number(props.data.progress?.progress) > 0 && (
                <div className="info-item progress-info">
                  {Math.floor(Number(props.data.progress?.progress))}%
                </div>
              )}
          </>
        )}
      </div>
      {!props.isProgress && (
        <div className="info-line">
          <div className="info-item">
            <div className="info-line-icon">
              <LevelIcon />
            </div>
            <div>{!!levelsTag?.length ? levelsTag?.join(" - ") : "No Level"}</div>
          </div>
        </div>
      )}
    </div>
  );
};

type ObjectCardProps = {
  object: ILibraryObject;
  hasLesson: boolean;
  ribbon: string;
  type: string;
  actionLabel: string;
  isProgress: boolean;
  isMyLearning?: boolean;
  isLocked?: boolean;
  onOpenPlan?: () => void;
};

const ObjectCard = (props: ObjectCardProps) => {
  const [like, setLike] = useState<boolean>(false);
  const { t } = useTranslation();

  const canFavorite = useMemo(() => {
    return (
      props.object.type !== "exercise" &&
      (isSelfStudyUser() || isGroupUser() || isOneOnOneUser())
    );
  }, [props.object]);

  const handleFavorite = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (like) {
      removeFavouriteCourse(props.object.id, () => {
        setLike(false);
      });
    } else {
      addFavouriteCourse(props.object.id, (resp: any) => {
        if (resp.code === "201 Created") {
          setLike(true);
        }
      });
    }
  };

  // Update the like status whenever object data is fetched
  useEffect(() => {
    setLike(!!props.object.is_favorite);
    // eslint-disable-next-line
  }, [props.object]);

  if (props.type === LibraryObjectViewType.HEADER && !props.object.description) {
    return null;
  }
  let image = null;

  if (props.hasLesson) {
    if (props.object.preview_url) {
      image = props.object.preview_url;
    } else {
      image = bookImg;
    }
  } else {
    if (props.type === LibraryObjectViewType.HEADER) {
      if (props.object.preview_url) {
        image = props.object.preview_url;
      }
    } else if (props.object.preview_vertical_url) {
      image = props.object.preview_vertical_url;
    } else {
      if (props.object.preview_url) {
        image = props.object.preview_url;
      } else {
        image = bookImg;
      }
    }
  }
  const previewImg =
    props.object.type === "exercise" ? (
      <div className="exercise-preview">
        <img src={exerciseIcon} alt="preview" className="exercise-icon" />
      </div>
    ) : (
      <img
        src={image}
        alt="preview"
        className={props.hasLesson ? "img-preview" : "preview_horizontal_img"}
      />
    );

  return (
    <div className={"object-card-wrapper relative " + props.type}>
      <LinkToObject
        object={props.object}
        isMyLearning={props.isMyLearning}
        isLocked={props.isLocked}
        onOpenPlan={props.onOpenPlan}
      >
        {props.isLocked && (
          <>
            <div className={"locked-layer"}>
              <HStack justify={"center"} align={"center"} style={{ height: "100%" }}>
                <LockIcon />
              </HStack>
            </div>

            {props.isLocked && <div className={"pro-course-badge"}>Pro access</div>}

            <div className="object-card-type-locked">
              {t(
                props.object.type === LibraryObjectType.COURSE
                  ? "Dashboard.Course"
                  : "Dashboard.Lesson"
              )}
            </div>
          </>
        )}

        {props.ribbon && (
          <div className="ribbon">
            <span>{props.ribbon}</span>
          </div>
        )}
        <div
          className={classnames("object-card", {
            "object-progress-card": props.isProgress,
          })}
        >
          <div className={`preview-container`}>{previewImg}</div>
          <div className="object-details">
            <div className="author-container">
              {!props.isLocked && (
                <div className="object-title horizontal">
                  <div className="object-card-type">
                    {t(
                      props.object.type === LibraryObjectType.COURSE
                        ? "Dashboard.Course"
                        : "Dashboard.Lesson"
                    )}
                  </div>
                  {props.type !== LibraryObjectViewType.HEADER && canFavorite && (
                    <button
                      className={"bookmark-btn"}
                      onClick={handleFavorite}
                      style={{ flexShrink: 0 }}
                    >
                      {like ? <FavouriteIcon /> : <FavouriteOutlinedIcon />}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="object-card-content">
              <div className="object-card-title">
                <span>{props.object.name}</span>
              </div>
              <ObjectCardInfo
                data={props.object}
                isProgress={props.isProgress}
                type={"library-object"}
              />
            </div>
          </div>
        </div>
        {props.type === LibraryObjectViewType.HEADER && (
          <img src={notchImg} alt="" className={props.type} />
        )}
      </LinkToObject>
    </div>
  );
};

export default ObjectCard;

type LinkToObjectProps = {
  object: ILibraryObject;
  isMyLearning?: boolean;
  isLocked?: boolean;
  onOpenPlan?: () => void;
  children: ReactNode;
};

const LinkToObject = (props: LinkToObjectProps) => {
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  if (props.isMyLearning) {
    const from = query.get("from");
    if (from !== "my_learning") {
      query.set("from", "my_learning");
    }
  }
  let path = "",
    search = "";

  if (props.object?.assignment?.id) {
    path = TalaeraRoutes.buildExerciseAssignmentDetailsPath(props.object.assignment.id);
  } else if (props.object?.type === "exercise") {
    path = TalaeraRoutes.buildExerciseDetailsPath(props.object.id);
  } else if (props.object?.type === LibraryObjectType.COURSE) {
    path = TalaeraRoutes.buildCourseObjectPath(props.object.id, "");
    search = query.toString() ? `?${query.toString()}` : "";
  } else {
    path = TalaeraRoutes.buildLibraryObjectPath(props.object.id, "", "");
    search = query.toString() ? `?${query.toString()}` : "";
  }

  return (
    <Link className="btn" to={{ pathname: path || "", search }}>
      {props.children}
    </Link>
  );
};
